import React, { useState, useEffect, useCallback } from 'react';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getChecklistsOnce } from '../../services/checklistService';
import {
  createFinancementDossier,
  updateFinancementDossierChecklist,
  cancelFinancementDossier,
} from '../../services/financementDossierService';
import { X } from 'lucide-react';

function formatDossierLabel(d, t) {
  const name = d.checklistName && String(d.checklistName).trim();
  if (name) return name;
  if (d.createdAt) {
    const date = d.createdAt?.toDate ? d.createdAt.toDate() : new Date(d.createdAt);
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return t('financementDossier.requested');
}

const RequestDossierModal = ({ property, onClose, onRequested, existingDossiers = [], onOpenDossier = null }) => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [checklists, setChecklists] = useState([]);
  const [selectedChecklistId, setSelectedChecklistId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updateForDossierId, setUpdateForDossierId] = useState(null);
  const [cancelConfirmDossierId, setCancelConfirmDossierId] = useState(null);

  useEffect(() => {
    if (!currentUser?.uid) return;
    getChecklistsOnce(currentUser.uid).then(setChecklists);
  }, [currentUser?.uid]);

  const handleCreate = async () => {
    if (!selectedChecklistId || !property?.id || !property?.uid || !currentUser?.uid) return;
    const checklist = checklists.find((c) => c.id === selectedChecklistId);
    if (!checklist?.items?.length) {
      setError(t('financementDossier.selectChecklistWithItems'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await createFinancementDossier({
        propertyId: property.id,
        ownerUid: property.uid,
        brokerUid: currentUser.uid,
        brokerDisplayName: currentUser.displayName || null,
        checklistId: checklist.id,
        checklistName: checklist.name || null,
        checklistSnapshot: { items: checklist.items },
      });
      onRequested?.();
      setSelectedChecklistId('');
    } catch (e) {
      setError(e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChecklist = async (dossierId) => {
    if (!dossierId || !selectedChecklistId) return;
    const checklist = checklists.find((c) => c.id === selectedChecklistId);
    if (!checklist?.items?.length) {
      setError(t('financementDossier.selectChecklistWithItems'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await updateFinancementDossierChecklist(dossierId, checklist.id, { items: checklist.items }, checklist.name || null);
      onRequested?.();
      setUpdateForDossierId(null);
    } catch (e) {
      setError(e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (dossierId) => {
    if (!dossierId) return;
    setLoading(true);
    setError('');
    try {
      await cancelFinancementDossier(dossierId);
      setCancelConfirmDossierId(null);
      onRequested?.();
    } catch (e) {
      setError(e?.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const hasExisting = existingDossiers.length > 0;

  const handleEscape = useCallback(() => {
    if (cancelConfirmDossierId) {
      setCancelConfirmDossierId(null);
      setError('');
      return;
    }
    if (updateForDossierId) {
      setUpdateForDossierId(null);
      return;
    }
    onClose();
  }, [cancelConfirmDossierId, updateForDossierId, onClose]);

  useRegisterTopEscapeClose(handleEscape);

  return (
    <div className="fixed inset-0 z-[220] rentalyzer-modal-overlay flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{t('financementDossier.request')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700"
            aria-label={t('cancel')}
          >
            <X size={20} />
          </button>
        </div>

        {/* Always show: send new checklist */}
        <p className="text-gray-600 text-sm mb-4">{t('financementDossier.requestDescription')}</p>
        <label className="block mb-4">
          <span className="text-sm font-medium text-gray-700">{t('financementDossier.selectChecklist')}</span>
          <select
            value={selectedChecklistId}
            onChange={(e) => setSelectedChecklistId(e.target.value)}
            className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
          >
            <option value="">—</option>
            {checklists.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name || c.id} {c.isDefault ? `(${t('checklists.isDefault')})` : ''}
              </option>
            ))}
          </select>
        </label>
        {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={handleCreate}
            disabled={loading || !selectedChecklistId}
            className="flex-1 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading && !updateForDossierId && !cancelConfirmDossierId ? '…' : t('financementDossier.request')}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
            {t('cancel')}
          </button>
        </div>

        {/* Existing requests for this property */}
        {hasExisting && (
          <>
            <h3 className="text-sm font-medium text-gray-700 mb-2">{t('financementDossier.existingRequestsForProperty')}</h3>
            <p className="text-gray-500 text-xs mb-3">{t('financementDossier.manageExistingHint')}</p>
            <ul className="space-y-3">
              {existingDossiers.map((d) => (
                <li key={d.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-800">{formatDossierLabel(d, t)}</span>
                    <span className="text-xs text-gray-500">{t(`financementDossier.status_${d.status || 'not_started'}`)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {onOpenDossier && (
                      <button
                        type="button"
                        onClick={() => { onOpenDossier(d); onClose?.(); }}
                        className="text-sm px-3 py-1.5 text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
                      >
                        {t('financementDossier.open')}
                      </button>
                    )}
                    {d.status !== 'submitted' && (
                      <>
                        <button
                          type="button"
                          onClick={() => setUpdateForDossierId(updateForDossierId === d.id ? null : d.id)}
                          disabled={loading}
                          className="text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                        >
                          {t('financementDossier.updateChecklist')}
                        </button>
                        {cancelConfirmDossierId === d.id ? (
                          <span className="inline-flex gap-1">
                            <button
                              type="button"
                              onClick={() => handleCancelRequest(d.id)}
                              disabled={loading}
                              className="text-sm px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                            >
                              {t('confirm')}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setCancelConfirmDossierId(null); setError(''); }}
                              className="text-sm px-2 py-1 border rounded hover:bg-gray-100"
                            >
                              {t('cancel')}
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setCancelConfirmDossierId(d.id)}
                            className="text-sm px-3 py-1.5 text-red-600 border border-red-200 rounded hover:bg-red-50"
                          >
                            {t('financementDossier.cancelRequest')}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {updateForDossierId === d.id && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <label className="block mb-2">
                        <span className="text-xs font-medium text-gray-600">{t('financementDossier.selectChecklist')}</span>
                        <select
                          value={selectedChecklistId}
                          onChange={(e) => setSelectedChecklistId(e.target.value)}
                          className="mt-1 block w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                        >
                          <option value="">—</option>
                          {checklists.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name || c.id}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleUpdateChecklist(d.id)}
                          disabled={loading || !selectedChecklistId}
                          className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                          {loading ? '…' : t('financementDossier.updateChecklist')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setUpdateForDossierId(null)}
                          className="text-sm px-3 py-1.5 border rounded hover:bg-gray-100"
                        >
                          {t('cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default RequestDossierModal;
